import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, Menu, X, ArrowRight, User, Settings, LogOut, Shield, Loader2 } from 'lucide-react';
import SolaLogo from './SolaLogo';
import { Button } from './ui/button';
import { useAppContext } from '@/contexts/AppContext';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { useGlobalSearch } from '@/hooks/useGlobalSearch';
import churchImg from '@/assets/church-peaceful.jpg';
import { stripPreacherTitle } from '@/lib/utils';
import { createPortal } from 'react-dom';

const GlobalHeader = ({ hideOnAuth = false }: { hideOnAuth?: boolean }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, signOut, isAdmin } = useAppContext();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { results, loading: searchLoading } = useGlobalSearch(searchQuery);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  // Cmd/Ctrl + K to open search
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === 'Escape' && searchOpen) setSearchOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [searchOpen]);

  const NAV_LINKS = [
    { label: 'Artigos', href: '/artigos' },
    { label: 'Biblioteca', href: '/biblioteca' },
    { label: 'Como funciona', href: '/como-funciona' },
    { label: 'Sobre', href: '/sobre' },
  ];

    const isAppPath = location.pathname === '/app' || 
                     location.pathname.startsWith('/generate') || 
                     location.pathname.startsWith('/profile') ||
                     location.pathname.startsWith('/assinatura') ||
                     (location.pathname.startsWith('/note/') && user) ||
                     location.pathname.startsWith('/curadoria') ||
                     location.pathname.startsWith('/colecoes') ||
                     location.pathname.startsWith('/livros') ||
                     location.pathname.startsWith('/em-alta') ||
                     location.pathname.startsWith('/busca') ||
                     location.pathname.startsWith('/jornada');
 
   if (isAppPath) {
     return null;
   }
 
   if (hideOnAuth && (location.pathname === '/login' || location.pathname === '/signup' || location.pathname === '/forgot-password')) {
     return null;
   }

  const firstName = profile?.display_name?.split(' ')[0] || 'Usuário';

  const handleSelectSearchResult = (noteId: string) => {
    setSearchOpen(false);
    navigate(`/note/${noteId}`);
  };

  const MATCH_LABEL: Record<string, string> = {
    title: 'Título',
    preacher: 'Pregador',
    theme: 'Tema',
    verse: 'Versículo',
    annotation: 'Anotação',
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || mobileMenuOpen 
          ? 'bg-background/95 backdrop-blur-md border-b border-border shadow-sm py-2' 
          : 'bg-background py-4'
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex items-center justify-between">
          {/* Esquerda: Logo */}
          <div className="flex items-center gap-8">
            <SolaLogo size="sm" showWordmark={true} />
            
            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-6">
              {NAV_LINKS.map((link) => (
                <Link 
                  key={link.href} 
                  to={link.href}
                  className={`text-sm font-medium transition-colors hover:text-accent ${
                    location.pathname === link.href ? 'text-accent' : 'text-foreground/70'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Direita: Search, Auth, Mobile Menu */}
          <div className="flex items-center gap-2 sm:gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => setSearchOpen(true)}
              className="text-foreground/70"
              aria-label="Buscar"
            >
              <Search className="h-5 w-5" />
            </Button>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full p-0" aria-label="Abrir menu do usuário">
                    <Avatar className="h-9 w-9 border border-border">
                      <AvatarImage src={profile?.avatar_url || ''} alt={profile?.display_name || ''} />
                      <AvatarFallback className="bg-accent/10 text-accent font-serif text-xs">
                        {firstName[0]}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <div className="flex items-center justify-start gap-2 p-2">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">{profile?.display_name || 'Membro'}</p>
                      <p className="text-xs leading-none text-muted-foreground">{user.email}</p>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/app')}>
                    <ArrowRight className="mr-2 h-4 w-4" />
                    Acessar Plataforma
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/profile')}>
                    <User className="mr-2 h-4 w-4" />
                    Meu Perfil
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem onClick={() => navigate('/admin')}>
                      <Shield className="mr-2 h-4 w-4" />
                      Painel Admin
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={signOut}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Sair
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => navigate('/login')}>
                  Entrar
                </Button>
                <Button size="sm" onClick={() => navigate('/signup')}>
                  Começar agora
                </Button>
              </div>
            )}

            {/* Mobile Menu Toggle */}
            <Button 
              variant="ghost" 
              size="icon" 
              className="lg:hidden" 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Fechar menu' : 'Abrir menu'}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu Content */}
        {mobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-background border-b border-border p-6 animate-in slide-in-from-top duration-300">
            <div className="flex flex-col gap-4">
              <nav className="flex flex-col gap-4">
                {NAV_LINKS.map((link) => (
                  <Link 
                    key={link.href} 
                    to={link.href}
                    className={`text-lg font-medium transition-colors hover:text-accent ${
                      location.pathname === link.href ? 'text-accent' : 'text-foreground/80'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>
              {!user && (
                <div className="flex flex-col gap-3 mt-6 pt-6 border-t border-border">
                  <Button variant="outline" className="w-full" onClick={() => navigate('/login')}>
                    Entrar
                  </Button>
                  <Button className="w-full" onClick={() => navigate('/signup')}>
                    Criar conta
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Global Search Portal */}
      {searchOpen && createPortal(
        <div 
          className="fixed inset-0 z-[100] bg-background/80 backdrop-blur-sm flex items-start justify-center pt-[10vh] px-4 animate-in fade-in duration-150"
          onClick={() => setSearchOpen(false)}
        >
          <div 
            className="w-full max-w-xl bg-card border border-border rounded-2xl shadow-2xl flex flex-col max-h-[80%] animate-in zoom-in-95 slide-in-from-top-4 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
              <Search size={18} className="text-muted-foreground flex-shrink-0" />
              <input
                autoFocus
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Buscar artigos, séries, livros..."
                className="flex-1 bg-transparent outline-none text-base text-foreground placeholder:text-muted-foreground"
              />
              {searchLoading && <Loader2 size={16} className="animate-spin text-muted-foreground" />}
              <Button variant="ghost" size="icon" onClick={() => setSearchOpen(false)} className="h-8 w-8" aria-label="Fechar busca">
                <X size={16} />
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto overscroll-contain pb-4">
              {searchQuery.trim().length > 0 && searchQuery.trim().length < 2 && (
                <p className="px-4 py-8 text-center text-sm text-muted-foreground">Digite pelo menos 2 caracteres…</p>
              )}
              {searchQuery.trim().length >= 2 && !searchLoading && results.length === 0 && (
                <p className="px-4 py-8 text-center text-sm text-muted-foreground">Nenhum resultado para "{searchQuery}"</p>
              )}
              {results.length > 0 && (
                <ul className="divide-y divide-border">
                  {results.map(r => (
                    <li key={r.noteId}>
                      <button
                        onClick={() => handleSelectSearchResult(r.noteId)}
                        className="w-full text-left px-4 py-3 flex gap-4 hover:bg-secondary/40 transition-colors group"
                      >
                        <img
                          src={r.cover_image_url || churchImg}
                          alt=""
                          className="h-14 w-14 rounded-lg object-cover flex-shrink-0"
                          loading="lazy"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="font-serif text-sm text-foreground leading-tight truncate group-hover:text-accent transition-colors">{r.title}</p>
                          <p className="text-xs text-muted-foreground truncate mt-1">
                            {stripPreacherTitle(r.preacher)} · {r.biblical_text}
                          </p>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {r.matchedIn.map(m => (
                              <span key={m} className="text-[9px] px-1.5 py-0.5 rounded-full bg-accent/10 text-accent font-medium uppercase tracking-wider">
                                {MATCH_LABEL[m]}
                              </span>
                            ))}
                          </div>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </header>
  );
};

export default GlobalHeader;
